import type { NovelSummary } from "../apiClient.js";
import { novelScript, scriptOfLanguage } from "./novelScript.js";

/**
 * Novels pinned to the landing page, by their permanent short code (the
 * `<code>` in `/novel/<slug>-<code>` and `/n/<code>`), in the order they are
 * shown: the first one present in the catalog that this browser has not
 * finished is the hero's featured novel — the one "Play now" opens — and the
 * rest lead the showcase under the hero (see `pickLandingNovels`).
 *
 * Why pins: the landing used to feature the most-liked novel, but a like needs
 * an account and almost nobody has one yet, so that choice was effectively
 * random. The pins are English stories (the first audience) whose owner can
 * edit them. Edit this list to change what a new visitor is offered first; a
 * code that is no longer in the public catalog (unpublished, or beyond the
 * catalog page the app has loaded) is simply skipped.
 *
 * - 22cefs — "The Tuesday Thief of Briarwhistle" (fantasy)
 * - 5yfx69 — "Up Troublesome Creek" (historical)
 * - h9t3n2 — "Forty Desks on Okishima"
 * - wxdvjy — "The Bechstein That Wasn't Cursed"
 */
export const FEATURED_NOVEL_CODES: readonly string[] = ["22cefs", "5yfx69", "h9t3n2", "wxdvjy"];

/** How many cards the landing showcase shows (one row on desktop, 2×2 on phones). */
export const LANDING_SHOWCASE_SIZE = 4;

export interface LandingSelection {
  /** The hero's novel; null when the catalog has nothing to feature. */
  featured: NovelSummary | null;
  /** The showcase under the hero: never repeats the featured novel. */
  showcase: NovelSummary[];
}

export interface LandingPickOptions {
  /** The UI language: after the pins, novels written in its script come first. */
  language: string;
  /** Whether this browser has finished a novel (lib/storage `isNovelFinished`). */
  isFinished?: (novelId: string) => boolean;
  pins?: readonly string[];
  size?: number;
}

/**
 * Picks the landing's featured novel and showcase from the public catalog feed
 * (the server's ranked order — never by likes).
 *
 * Featured — what "Play now" opens: the first pin this browser has not
 * finished, else the feed's first unfinished novel written in the UI language's
 * script (lib/novelScript.ts), else the first pin; with no pin in the catalog,
 * the feed's first novel in the UI script, else the feed's first.
 *
 * Showcase: the other pins in pin order, then the feed's novels in the UI
 * script, then the rest of the feed, in feed order, without duplicates and never
 * the featured novel, capped at `size`. So an English visitor is not shown a
 * Russian story while an English one is left, even when the feed ranks it first.
 */
export function pickLandingNovels(
  catalog: readonly NovelSummary[],
  {
    language,
    isFinished = () => false,
    pins = FEATURED_NOVEL_CODES,
    size = LANDING_SHOWCASE_SIZE
  }: LandingPickOptions
): LandingSelection {
  const feed: NovelSummary[] = [];
  const seen = new Set<string>();
  for (const novel of catalog) {
    if (seen.has(novel.id)) continue;
    seen.add(novel.id);
    feed.push(novel);
  }

  const byCode = new Map<string, NovelSummary>();
  for (const novel of feed) {
    if (novel.short_code && !byCode.has(novel.short_code)) byCode.set(novel.short_code, novel);
  }
  const pinned = [...new Set(pins)]
    .map((code) => byCode.get(code))
    .filter((novel): novel is NovelSummary => novel !== undefined);

  const script = scriptOfLanguage(language);
  const inScript = feed.filter((novel) => novelScript(novel) === script);
  const unfinished = (novel: NovelSummary) => !isFinished(novel.id);

  const featured =
    pinned.find(unfinished) ??
    inScript.find(unfinished) ??
    pinned[0] ??
    inScript[0] ??
    feed[0] ??
    null;

  const showcase: NovelSummary[] = [];
  const shown = new Set<string>(featured ? [featured.id] : []);
  for (const novel of [...pinned, ...inScript, ...feed]) {
    if (showcase.length >= Math.max(0, size)) break;
    if (shown.has(novel.id)) continue;
    shown.add(novel.id);
    showcase.push(novel);
  }
  return { featured, showcase };
}
